import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, IconButton, Tabs, Tab,
  Link as MuiLink, Tooltip, LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import ConfirmationNumberRoundedIcon from '@mui/icons-material/ConfirmationNumberRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';

import useResource from './components/useResource';
import OverviewTab from './tabs/OverviewTab';
import ActivityTab from './tabs/ActivityTab';
import KnowledgeTab from './tabs/KnowledgeTab';
import TechnicalHealthTab from './tabs/TechnicalHealthTab';
import PlaceholderTab from './tabs/PlaceholderTab';

const TABS = [
  { key: 'overview',  label: 'Overview' },
  { key: 'activity',  label: 'Activity' },
  { key: 'health',    label: 'Technical Health' },
  { key: 'knowledge', label: 'Knowledge' },
  { key: 'gaps',      label: 'Gaps' },
];
const TAB_KEYS = TABS.map(t => t.key);

/**
 * Customer 365 — a five-tab view for one account (Overview · Activity ·
 * Technical Health · Knowledge · Gaps). The active tab is persisted in the URL
 * (?tab=). The shell owns the shared account record plus the contracts and open
 * tickets used by both the header stat chips and the Overview / Activity tabs,
 * so those load once; every other section loads independently inside its tab.
 */
function Detail() {
  const { code } = useParams();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const currentTab = TAB_KEYS.includes(requested) ? requested : 'overview';

  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(() => new Date());
  const refresh = () => { setRefreshKey(k => k + 1); setLastRefreshed(new Date()); };

  const accountRes  = useResource(`cma/account?code=${encodeURIComponent(code)}`, { refreshKey });
  const companyRes  = useResource(`autotask/company?account=${encodeURIComponent(code)}`, { refreshKey });
  const contractsRes = useResource(`autotask/contract?account=${encodeURIComponent(code)}`, { refreshKey });
  const ticketsRes   = useResource(`autotask/ticket?account=${encodeURIComponent(code)}&include_hours=false`, { refreshKey });

  const account = useMemo(() => {
    const list = Array.isArray(accountRes.data) ? accountRes.data : [];
    return list.find(a => a.code === code) || list[0] || null;
  }, [accountRes.data, code]);

  const company = useMemo(() => {
    const list = Array.isArray(companyRes.data) ? companyRes.data : [];
    return list.find(c => String(c.id) === String(account?.autotask_id)) || list[0] || null;
  }, [companyRes.data, account]);
  const autotaskUrl = company?.autotaskUrl || null;

  const customerName = account?.name || code;
  const contractCount = Array.isArray(contractsRes.data) ? contractsRes.data.length : null;
  const ticketCount = Array.isArray(ticketsRes.data) ? ticketsRes.data.length : null;

  const handleTab = (e, val) => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('tab', val);
      return p;
    });
  };

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Link to="/customer" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Customer Center</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{customerName}</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box sx={{ flex: 1 }}>
                {(() => {
                  const nameSx = {
                    fontWeight: 700,
                    background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  };
                  const nameNode = <Typography variant="h5" sx={nameSx}>{customerName}</Typography>;
                  return autotaskUrl ? (
                    <Tooltip title="Open in Autotask">
                      <MuiLink href={autotaskUrl} target="_blank" rel="noopener noreferrer" underline="none"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75,
                          '&:hover': { opacity: 0.85 } }}>
                        {nameNode}
                        <OpenInNewRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                      </MuiLink>
                    </Tooltip>
                  ) : nameNode;
                })()}
                <Typography variant="body2" color="text.secondary">
                  Account: {code}{account?.autotask_id ? ` · Autotask #${account.autotask_id}` : ''}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip size="small" icon={<ConfirmationNumberRoundedIcon />}
                  label={`${ticketCount ?? '—'} open ticket${ticketCount === 1 ? '' : 's'}`}
                  variant="outlined" />
                <Chip size="small" icon={<DescriptionRoundedIcon />}
                  label={`${contractCount ?? '—'} active contract${contractCount === 1 ? '' : 's'}`}
                  variant="outlined" />
                <Chip size="small" icon={<ShieldRoundedIcon />}
                  label="Risk: not scored" variant="outlined" color="default"
                  title="Risk posture scoring arrives in Phase 5" />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Updated {lastRefreshed.toLocaleTimeString()}
                </Typography>
                <IconButton size="small" onClick={refresh} disabled={accountRes.loading} title="Refresh">
                  <RefreshIcon/>
                </IconButton>
              </Stack>
            </Stack>
            {accountRes.loading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {accountRes.error && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">
              Could not load account {code} — {accountRes.error}
            </Typography>
          </Paper>
        )}

        <Box sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={currentTab} onChange={handleTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            {TABS.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>
        </Box>

        <Box sx={{ py: 3 }}>
          {currentTab === 'overview' && (
            <OverviewTab code={code} account={account} company={companyRes} contracts={contractsRes} refreshKey={refreshKey} />
          )}
          {currentTab === 'activity' && (
            <ActivityTab code={code} account={account} tickets={ticketsRes} refreshKey={refreshKey} />
          )}
          {currentTab === 'health' && (
            <TechnicalHealthTab code={code} classification={company?.classificationName} refreshKey={refreshKey} />
          )}
          {currentTab === 'knowledge' && (
            <KnowledgeTab code={code} account={account} />
          )}
          {currentTab === 'gaps' && (
            <PlaceholderTab
              title="Gaps"
              message="The compliance scorecard, baseline score and knowledge-gap summary are coming in Phase 4."
              phase="Phase 4"
            />
          )}
        </Box>
      </Container>
    </Box>
  );
}

export default Detail;
